import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { FormField, SelectField } from '@components/web/FormField'
import Button from '@components/web/Button'
import PasswordStrength from '@components/web/PasswordStrength'
import { usePatientAuth } from '@hooks/usePatientAuth'
import { usePageMeta } from '@hooks/usePageMeta'

interface FormData {
  name: string
  email: string
  phoneNumber: string
  dateOfBirth: string
  gender: string
  password: string
  confirmPassword: string
}

interface FormErrors {
  name?: string
  email?: string
  phoneNumber?: string
  dateOfBirth?: string
  gender?: string
  password?: string
  confirmPassword?: string
  general?: string
}

const GENDER_OPTIONS = [
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
  { value: 'Other', label: 'Other' },
  { value: 'Prefer not to say', label: 'Prefer not to say' },
]

export default function SignUp() {
  const navigate = useNavigate()
  const { signup, validateEmail, validatePassword, validatePhoneNumber } = usePatientAuth()
  usePageMeta({
    title: 'HomiCareplus - Professional Healthcare at Your Doorstep',
    description: 'Create a HomiCareplus patient account to book in-home care, manage appointments, and access a smoother digital care experience.',
  })
  const [isLoading, setIsLoading] = useState(false)
  const [form, setForm] = useState<FormData>({
    name: '', email: '', phoneNumber: '', dateOfBirth: '', gender: '', password: '', confirmPassword: '',
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const maxDateOfBirth = new Date().toISOString().split('T')[0]

  const set = (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }))

  const validate = (): boolean => {
    const errs: FormErrors = {}
    if (!form.name.trim() || form.name.trim().length < 2) errs.name = 'Full name required (min 2 characters)'
    if (!validateEmail(form.email)) errs.email = 'Enter a valid email address'
    if (!validatePhoneNumber(form.phoneNumber)) errs.phoneNumber = 'Enter a valid phone number (min 10 digits)'
    if (!form.dateOfBirth) {
      errs.dateOfBirth = 'Date of birth is required'
    } else if (form.dateOfBirth > maxDateOfBirth) {
      errs.dateOfBirth = 'Date of birth cannot be in the future'
    }
    if (!form.gender) errs.gender = 'Gender is required'
    const { isValid, errors: pwdErrors } = validatePassword(form.password)
    if (!isValid) errs.password = pwdErrors[0]
    if (form.password !== form.confirmPassword) errs.confirmPassword = 'Passwords do not match'
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault()
    if (!validate()) return
    setIsLoading(true)
    const result = await signup({
      name: form.name.trim(),
      email: form.email.trim().toLowerCase(),
      phoneNumber: form.phoneNumber.trim(),
      dateOfBirth: form.dateOfBirth,
      gender: form.gender,
      password: form.password,
    })
    setIsLoading(false)
    if (!result.success) {
      setErrors({ general: result.error || 'Signup failed. Please try again.' })
      return
    }
    navigate(`/auth/verify-email?email=${encodeURIComponent(form.email)}&role=patient`)
  }

  return (
    <div className="bg-[#fcfff8] rounded shadow-lg p-5 sm:p-6 lg:p-7 w-full">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft size={16} /> Back
      </Link>

      <div className="mb-5 mt-6 pb-4 text-center">
        <h2 className="text-2xl font-bold text-foreground">Create Your Patient Account</h2>
        <p className="text-muted-foreground text-sm mt-1">Join HomiCareplus for better healthcare access and management.</p>
      </div>

      {errors.general && (
        <div className="mb-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
          <p className="text-sm text-destructive">{errors.general}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label="Full Name" type="text" placeholder="Full Name" value={form.name} onChange={set('name')} error={errors.name} required autoComplete="name" className="h-11 rounded" />
          <FormField label="Email Address" type="email" placeholder="Email Address" value={form.email} onChange={set('email')} error={errors.email} required autoComplete="email" className="h-11 rounded" />
          <FormField label="Phone Number" type="tel" placeholder="Phone Number" value={form.phoneNumber} onChange={set('phoneNumber')} error={errors.phoneNumber} required autoComplete="tel" className="h-11 rounded" />
          <FormField label="Date of Birth" type="date" value={form.dateOfBirth} onChange={set('dateOfBirth')} error={errors.dateOfBirth} required max={maxDateOfBirth} className="h-11 rounded" />
          <div className="sm:col-span-2">
            <SelectField
              label="Gender"
              value={form.gender}
              onChange={(value) => setForm((prev) => ({ ...prev, gender: value }))}
              options={GENDER_OPTIONS}
              placeholder="Select gender"
              required
              error={errors.gender}
            />
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 sm:items-start">
          <div className="space-y-2">
            <FormField label="Password" type="password" placeholder="Min 12 characters" value={form.password} onChange={set('password')} error={errors.password} required autoComplete="new-password" className="h-11 rounded" />
            <PasswordStrength password={form.password} />
          </div>
          <FormField label="Confirm Password" type="password" placeholder="Re-enter your password" value={form.confirmPassword} onChange={set('confirmPassword')} error={errors.confirmPassword} required autoComplete="new-password" className="h-11 rounded" />
        </div>

        <Button type="submit" className="w-full mt-1 h-11 rounded" size="lg" isLoading={isLoading}>
          Create Account
        </Button>
      </form>

      <p className="text-center text-sm text-muted-foreground mt-5">
        Already have an account?{' '}
        <Link to="/auth/login?role=patient" className="text-primary font-semibold hover:underline">Login</Link>
      </p>
    </div>
  )
}
